import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { AppView, rem, withAnimatedNavigationHeader } from 'design-system';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import DepositAmountSelector from 'design-system/DepositAmountSelector';
import EmojiText from 'design-system/EmojiText';
import { IconBoldArrow } from 'design-system/icons';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import useFetch, { muteAllButThrows } from 'hooks/useFetch';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import ConnectedAccountRow from 'features/invest/components/ConnectedAccountRow';
import { selectConnectedAccountBalanceFromConnectedAccountWithBankId, useConnectedAccount, } from 'features/invest/hooks/selectors';
import { ConfirmTransferModal } from 'features/transfers/components/ConfirmTransferModal';
import TransferPotRow from 'features/transfers/components/TransferPotRow';
import { useTransfer } from 'features/transfers/hooks/useTransfer';
import { InternalTransfer } from 'features/transfers/types';
import { fetchIsaAllowance } from 'features/wealth/actions';
import { selectIsaAllowance } from 'features/wealth/selectors';
import { selectDefaultCurrency } from 'reducers/selectors';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import Sentry from 'utils/sentry';
import { depositPotFromDirectDebit } from '../actions';
import { AddMoneyPotPickerModal } from '../components/AddMoneyPotPickerModal';
import { ChooseAccountToDepositToPotModal } from '../components/ChooseAccountToDepositToPotModal';
import { MIN_POT_DEPOSIT_AMOUNT, POT_DEPOSIT_AMOUNT_OPTIONS, POTS_MAXIMUM_DD_DEPOSIT_LIMIT, POTS_MAXIMUM_OB_DEPOSIT_LIMIT, } from '../constants';
import { selectPotProductFromProductId, selectShouldShowPotsList, selectTransferPotsFromPotType, } from '../reducers/selectors';
const strings = {
    title: 'Deposit',
    exceedBalanceError: 'Your balance is too low',
    minAmountErrorPrefix: 'Minimum deposit is ',
    maxAmountErrorSuffix: ' maximum',
    isaAllowanceLeft: ' ISA allowance left this tax year',
    continue: 'Continue',
};
const getDefaultDepositSource = (connectedAccount, connectedAccountBalance) => {
    if (connectedAccount?.pispBankId) {
        if (connectedAccountBalance?.amount) {
            return { type: 'openBankingDeposit' };
        }
    }
    return {
        type: 'singleDirectDebit',
    };
};
const DepositToPotScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { pot, isCreatePotFlow, isAddMoneyFlow } = route.params;
    const store = useAppStore();
    const colors = useColors();
    const dispatch = useAppDispatch();
    const componentStyles = useStyles(styleSet);
    const toPickerSharedValue = useSharedValue(0);
    const fromPickerSharedValue = useSharedValue(0);
    const connectedAccount = useConnectedAccount();
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const [isToPickerOpen, setIsToPickerOpen] = useState(false);
    const [isFromPickerOpen, setIsFromPickerOpen] = useState(false);
    const [isSubmittingDirectDebit, setIsSubmittingDirectDebit] = useState(false);
    const [amountError, setAmountError] = useState();
    const connectedAccountBalance = useAppSelector(selectConnectedAccountBalanceFromConnectedAccountWithBankId);
    const [from, setFrom] = useState(() => getDefaultDepositSource(connectedAccount, connectedAccountBalance));
    const [to, setTo] = useState(pot);
    const potProduct = useAppSelector((store) => selectPotProductFromProductId(store, to.productId));
    const fromPot = from.type === 'pot' ? from.pot : undefined;
    const isaAllowance = useAppSelector(selectIsaAllowance);
    // Moving money between pots of the same Cash ISA does not use any allowance
    const isTransferWithinCashIsa = fromPot?.productId === to.productId;
    const remainingAllowance = potProduct?.isProductCashISA && !isTransferWithinCashIsa ? isaAllowance?.totalAllowanceLeft : undefined;
    // The wealth slice is not persisted, so the allowance may not have been fetched yet
    const isToCashIsa = !!potProduct?.isProductCashISA;
    const hasFetchedIsaAllowance = useRef(false);
    useEffect(() => {
        if (isToCashIsa && !hasFetchedIsaAllowance.current) {
            hasFetchedIsaAllowance.current = true;
            dispatch(fetchIsaAllowance());
        }
    }, [dispatch, isToCashIsa]);
    // this is used in to and must not include interest pot
    const potsOfTheSameTypeAsFrom = useAppSelector((state) => selectTransferPotsFromPotType(state, fromPot?.type, fromPot?.productId));
    const [amount, setAmount] = useState(() => {
        if (connectedAccountBalance && from?.type === 'openBankingDeposit') {
            if (connectedAccountBalance.amount >= 100) {
                return 100;
            }
            if (connectedAccountBalance.amount >= 50) {
                return 50;
            }
            return 0;
        }
        return 100;
    });
    const { onInternalTransfer, requesting } = useTransfer();
    const [, , , fetchDirectDebitDeposit] = useFetch(muteAllButThrows);
    const currencySymbol = getSymbolFromCurrency(defaultCurrency) || '';
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.title,
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.POTS_DEPOSIT}/>,
        });
    }, [colors.elements.primary, navigation]);
    const fromBalance = useMemo(() => {
        if (!from)
            return undefined;
        if (from.type === 'pot')
            return from.pot.withdrawableBalance || from.pot.balance;
        // Open Banking deposits can draw on an overdraft, so the bank balance is not a limit
        return undefined;
    }, [from]);
    const maxDepositLimit = from?.type === 'singleDirectDebit' ? POTS_MAXIMUM_DD_DEPOSIT_LIMIT : POTS_MAXIMUM_OB_DEPOSIT_LIMIT;
    const checkAmountError = useCallback((value) => {
        const isBankFunded = from?.type === 'openBankingDeposit' || from?.type === 'singleDirectDebit';
        if (isBankFunded && value < MIN_POT_DEPOSIT_AMOUNT) {
            setAmountError('belowMinimum');
        }
        else if (value > maxDepositLimit) {
            setAmountError('exceedMax');
        }
        else if (remainingAllowance != null && value > remainingAllowance) {
            setAmountError('exceedAllowance');
        }
        else if (fromBalance && value > fromBalance.value) {
            setAmountError('exceedBalance');
        }
        else {
            setAmountError(undefined);
        }
    }, [from, fromBalance, maxDepositLimit, remainingAllowance]);
    useEffect(() => {
        if (amount) {
            checkAmountError(amount);
        }
        else {
            setAmountError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAmountError]); // we want this hook to run only if checkAmountError updates
    const onFromPress = useCallback(() => {
        Keyboard.dismiss();
        setIsFromPickerOpen(true);
        ChooseAccountToDepositToPotModal.show(to, from, (selection) => {
            setFrom(selection);
            setIsFromPickerOpen(false);
        }, fromPickerSharedValue, () => setIsFromPickerOpen(false));
    }, [from, fromPickerSharedValue, to]);
    const showPotPicker = useCallback(() => {
        Keyboard.dismiss();
        setIsToPickerOpen(true);
        AddMoneyPotPickerModal.show(from, to, (selected) => {
            setTo(selected);
            setIsToPickerOpen(false);
        }, toPickerSharedValue, () => setIsToPickerOpen(false));
    }, [from, to, toPickerSharedValue]);
    const From = useMemo(() => {
        if (!from)
            return null;
        if (from.type === 'pot') {
            return (<TransferPotRow name={from.pot.name} onPress={onFromPress} isPickerModalOpen={isFromPickerOpen} Icon={<EmojiText>{from.pot.emoji}</EmojiText>} amount={from.pot.withdrawableBalance || from.pot.balance}/>);
        }
        if (!connectedAccount)
            return null;
        return (<ConnectedAccountRow onPress={onFromPress} account={connectedAccount} isPickerModalOpen={isFromPickerOpen} description={from.type === 'openBankingDeposit' ? 'Open Banking transfer' : 'Direct Debit'}/>);
    }, [connectedAccount, from, isFromPickerOpen, onFromPress]);
    const isToDisabled = useMemo(() => {
        if (!isAddMoneyFlow) {
            return true;
        }
        if (from.type === 'pot') {
            const candidates = potsOfTheSameTypeAsFrom.filter((candidate) => candidate.id !== fromPot?.id && candidate.id !== to.id);
            return candidates.length === 0;
        }
        // for non pot sources, the picker should only be active when there are more than 1 pots
        // potsOfTheSameTypeAsFrom returns all selectable pots by default
        return potsOfTheSameTypeAsFrom.length < 2;
    }, [from.type, fromPot?.id, isAddMoneyFlow, potsOfTheSameTypeAsFrom, to.id]);
    const To = useMemo(() => (<TransferPotRow name={to.name} onPress={showPotPicker} disabled={isToDisabled} isPickerModalOpen={isToPickerOpen} Icon={<EmojiText>{to.emoji}</EmojiText>} amount={to.withdrawableBalance || to.balance}/>), [isToDisabled, isToPickerOpen, showPotPicker, to.balance, to.emoji, to.name, to.withdrawableBalance]);
    const handleAmountChange = useCallback((value) => {
        setAmount(value);
        checkAmountError(value);
    }, [checkAmountError]);
    const footnote = useMemo(() => {
        const isError = !!amountError;
        const colorProps = isError ? { Red: true } : { Secondary: true };
        let content;
        if (remainingAllowance != null) {
            content = (<>
            <Text NumbersThin-14 {...colorProps}>
              {formatAmount(remainingAllowance, to.balance.currency, 2)}
            </Text>
            {strings.isaAllowanceLeft}
          </>);
        }
        if (amountError === 'belowMinimum') {
            content = (<>
            {strings.minAmountErrorPrefix}
            <Text NumbersThin-14 Red>
              {currencySymbol}
              {formatAmount(MIN_POT_DEPOSIT_AMOUNT, undefined, 0)}
            </Text>
          </>);
        }
        else if (amountError === 'exceedMax') {
            content = (<>
            <Text NumbersThin-14 Red>
              {formatAmount(maxDepositLimit, 'GBP')}
            </Text>
            {strings.maxAmountErrorSuffix}
          </>);
        }
        else if (amountError === 'exceedBalance') {
            content = strings.exceedBalanceError;
        }
        if (!content)
            return undefined;
        return (<Text TextThin-14 {...colorProps}>
          {content}
        </Text>);
    }, [amountError, currencySymbol, maxDepositLimit, remainingAllowance, to.balance.currency]);
    const performDirectDebitDeposit = useCallback(async (depositAmount) => {
        try {
            setIsSubmittingDirectDebit(true);
            LoadingAppViewModal.show({ title: 'Processing your payment...' });
            const response = await fetchDirectDebitDeposit(depositPotFromDirectDebit(to.id, { value: depositAmount, currency: to.balance.currency }));
            const potScreen = navigation.getState().routes?.find((route) => route.name === 'Pot');
            const potFromScreenParams = potScreen?.params?.screenParams;
            if (potFromScreenParams) {
                // @ts-ignore - a route taken from state is valid
                navigation.popTo(potScreen.name, {
                    ...potScreen.params,
                    screenParams: {
                        ...potScreen.params.screenParams, // this is deliberate to change the pot object in screen params to trigger fetching of activities
                    },
                });
            }
            else {
                navigation.popToTop();
            }
            navigation.navigate('DepositTimeline', {
                emoji: to.emoji,
                potName: to.name,
                amount: depositAmount,
                isBankTransfer: true,
                potProductId: to.productId,
                timestamp: new Date().toISOString(),
                depositEta: response?.depositEta,
                depositDetails: { method: 'singleDirectDebit' },
                // eslint-disable-next-line no-nested-ternary
                potId: isCreatePotFlow ? (selectShouldShowPotsList(store.getState()) ? to.id : undefined) : undefined,
            });
        }
        catch (e) {
            Sentry.captureException(e);
        }
        finally {
            LoadingAppViewModal.hide();
            setIsSubmittingDirectDebit(false);
        }
    }, [fetchDirectDebitDeposit, isCreatePotFlow, to.balance.currency, to.emoji, to.id, to.name, to.productId]);
    const handleDirectDebitDeposit = useCallback((depositAmount) => {
        if (connectedAccount?.potsMandateStatus === 'INACTIVE' || connectedAccount?.potsMandateStatus === 'NONE') {
            navigation.navigate('AutosaveSelectFundingAccount', {
                account: connectedAccount,
                onDirectDebitSetup: () => performDirectDebitDeposit(depositAmount),
            });
            return;
        }
        performDirectDebitDeposit(depositAmount);
    }, [connectedAccount, performDirectDebitDeposit]);
    const submitDeposit = useCallback(() => {
        if (!from || !amount)
            return;
        if (from.type === 'pot') {
            onInternalTransfer(amount, from.pot, to, InternalTransfer.fund);
            return;
        }
        if (from.type === 'openBankingDeposit') {
            if (!connectedAccount)
                return;
            navigation.navigate('InvestCreateTopUp', {
                flow: 'topUpPot',
                destination: 'pot',
                bankAccount: connectedAccount,
                amount,
                backCount: 3,
                potId: to.id,
                reference: 'EMMA TRADING',
                isCreatePotFlow,
                onError: (userMessage) => Modal.showError(userMessage || 'Could not transfer to pot, please try again'),
            });
            return;
        }
        handleDirectDebitDeposit(amount);
    }, [from, amount, handleDirectDebitDeposit, onInternalTransfer, to, connectedAccount, isCreatePotFlow]);
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        if (!from || !amount)
            return;
        const confirmFrom = from.type === 'pot'
            ? from.pot
            : connectedAccount && { ...connectedAccount, itemType: 'investConnectedAccount' };
        if (!confirmFrom)
            return;
        ConfirmTransferModal.show(confirmFrom, to, amount, 'deposit', submitDeposit);
    }, [from, amount, connectedAccount, to, submitDeposit]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, componentStyles.contentContainer);
    const renderWithdrawInfo = useMemo(() => {
        if (potProduct) {
            if (to.type === 'INTEREST_NOTICE' || to.type === 'ACCUMULATED_INTEREST_NOTICE') {
                return (<>
              <Spacer h={16}/>
              <Text TextThin-14 Secondary>
                {`Money added to ${to.name} takes ${potProduct.noticeDays} days to withdraw, as it's a notice pot.`}
              </Text>
            </>);
            }
        }
        return null;
    }, [to.name, to.type, potProduct]);
    return (<AppView withFrame={false}>
        <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
          {From}
          <Spacer h={16}/>
          <View>
            {To}
            <View style={componentStyles.iconContainer} pointerEvents="none">
              <View style={componentStyles.iconWrapper}>
                <IconBoldArrow alt colorKey="brand" direction="bottom"/>
              </View>
            </View>
          </View>
          <Spacer h={16}/>
          <DepositAmountSelector min={1} amount={amount} max={maxDepositLimit} footnote={footnote} setAmount={handleAmountChange} options={POT_DEPOSIT_AMOUNT_OPTIONS} availableAmount={fromBalance?.value} remainingAllowance={remainingAllowance}/>
          <View style={componentStyles.withdrawableInfoContainer}>{renderWithdrawInfo}</View>
        </AnimatedKeyboardAwareScrollView>
        <KeyboardAvoidingButton brand isFetching={requesting || isSubmittingDirectDebit} title={strings.continue} onPress={handleContinue} disabled={!from || !amount || !!amountError}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: strings.title,
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    contentContainer: {
        paddingTop: rem(16),
    },
    withdrawableInfoContainer: {
        paddingHorizontal: rem(16),
    },
}));
export default DepositToPotScreen;
